import { el, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import { TREES, effectLabel, skillsByTree, type SkillDef } from "../game/skills";
import { progress } from "../store/progress";

export class SkillsScreen implements Screen {
  private off?: () => void;
  private topRefresh: () => void = () => {};
  constructor(private nav: Nav) {}

  mount(host: HTMLElement): void {
    const screen = el("div", "screen skills");
    const top = buildTopbar({
      title: "menu.skills",
      onBack: () => this.nav.home(),
      showStardust: true,
    });
    this.topRefresh = top.refresh;

    const body = el("div", "skills-body");
    screen.append(top.node, body);
    host.appendChild(screen);

    const render = (): void => {
      this.topRefresh();
      body.innerHTML = "";
      body.appendChild(this.buildGraph(render));
    };
    render();
    this.off = i18n.onChange(render);
  }

  /** Whole skill system as one tree: a root that branches into the three trees. */
  private buildGraph(render: () => void): HTMLElement {
    const graph = el("div", "skill-graph");

    // Decorative trunk node the three trees branch from (no level/stardust —
    // those live in the top bar and aren't tied to this node).
    const root = el("div", "skill-root");
    root.innerHTML = `<div class="root-core">✦</div>`;
    graph.appendChild(root);

    const branches = el("div", "skill-branches");
    for (const tree of TREES) branches.appendChild(this.buildBranch(tree, render));
    graph.appendChild(branches);
    return graph;
  }

  private buildBranch(tree: string, render: () => void): HTMLElement {
    const branch = el("div", `branch tree-${tree}`);
    branch.appendChild(el("div", "branch-head", t(`skill.tree.${tree}`)));
    const nodes = el("div", "branch-nodes");
    for (const def of skillsByTree(tree as SkillDef["tree"])) {
      nodes.appendChild(this.buildNode(def, render));
    }
    branch.appendChild(nodes);
    return branch;
  }

  private buildNode(def: SkillDef, render: () => void): HTMLElement {
    const level = progress.skillLevel(def.id);
    const maxed = level >= def.max;
    const cost = progress.skillNextCost(def.id);
    const affordable = cost != null && progress.snapshot.stardust >= cost;

    const node = el("div", `skill-node ${maxed ? "maxed" : ""}`);

    node.innerHTML = `
      <div class="skill-head">
        <span class="skill-icon">${def.icon}</span>
        <span class="skill-name">${t(`skill.${def.id}`)}</span>
      </div>
      <div class="skill-desc">${t(`skill.${def.id}.desc`)}</div>
      <div class="skill-level">Lv ${level}<span class="lv-max">/${def.max}</span></div>
      <div class="skill-effect">${level > 0 ? effectLabel(def, level) : "—"}${maxed ? ` · ${t("skill.max")}` : ""}</div>`;

    const btn = el("button", `skill-buy ${affordable ? "btn-primary" : ""}`);
    btn.innerHTML = maxed
      ? `<span class="maxed-tag">${t("skill.max")}</span>`
      : `<span class="sd-icon">✦</span> ${cost}`;
    btn.disabled = !affordable;
    btn.onclick = () => {
      if (btn.disabled || !progress.upgradeSkill(def.id)) return;
      // Press + level-up pop before the graph refreshes, so the tap feels good
      // (a plain re-render would swallow the :active state instantly).
      btn.animate(
        [{ transform: "scale(1)" }, { transform: "scale(0.85)" }, { transform: "scale(1)" }],
        { duration: 200, easing: "ease-out" },
      );
      node.animate(
        [
          { transform: "scale(1)", filter: "brightness(1)" },
          { transform: "scale(1.07)", filter: "brightness(1.5)" },
          { transform: "scale(1)", filter: "brightness(1)" },
        ],
        { duration: 260, easing: "cubic-bezier(0.2,0.9,0.3,1.1)" },
      );
      setTimeout(render, 170);
    };
    node.appendChild(btn);
    return node;
  }

  unmount(): void {
    this.off?.();
  }
}
